import React from 'react';
import {
    Page,
    SkeletonBodyText,
    Card,
    Banner,
    Link,
    Select,
    TextField,
    ChoiceList,
    Stack
} from '@shopify/polaris';
import OperationPreviewComponent from '../../components/OperationPreviewComponent';
import TagViewComponent from '../../components/TagViewComponent';
import { Helmet } from 'react-helmet';
import { apiFetch, ResourceTypes, resourceTypeToDataPermissions } from '../../common/Helpers';
import { RequiredPermissionsWarning } from '../../common/RequiredPermissionsWarning';

const PrefixDelimitCharacterOptions = [
    { label: "';' - Semicolon", value: "Semicolon" },
    { label: "':' - Colon", value: "Colon" },
    { label: "'-' - Dash", value: "Dash" },
    { label: "'_' - Underscore", value: "Underscore" },
];

const ModeOptions = [
    { label: "Start Of Text", value: "StartOfText" },
    { label: "End Of Text", value: "EndOfText" },
    { label: "Anywhere", value: "Anywhere" },
];

class ScenarioTagTextReplacementScenarioPage extends React.Component {

    state = {
        step: null,
        resourceType: "Product",
        mode: "Anywhere",
        textReplacement: "",
        textToReplace: "",
        tagPrefixBehavior: null,
        tagPrefixBehaviorValue: "",
        selectedTags: [],
        isLoading: true,
        preview: null,
    };

    render() {

        if (this.state.step == null) {
            return (
                <div>
                    <Helmet>
                        <title>Tag Genie - Scenario - Tag Text Replacement</title>
                    </Helmet>
                    <Page title="Tag Text Replacement">
                        <SkeletonBodyText lines="20" />
                    </Page>
                </div>)
        }

        let resourcePlural = "products";
        let resourceSingular = "product";

        if (this.state.resourceType === "Customer") {
            resourcePlural = "customers";
            resourceSingular = "customer";
        }

        if (this.state.resourceType === "Order") {
            resourcePlural = "orders";
            resourceSingular = "order";
        }

        let tags = this.getTags();

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Scenario - Tag Text Replacement</title>
                </Helmet>
                {this.state.step === "create" &&
                    <Page title="Tag Text Replacement">
                        <Card primaryFooterAction={{ content: 'Preview Changes', onAction: this.handlePreview, loading: this.state.isLoading }}>
                            <Card.Section>
                                <Banner>
                                    <p>If you require more advanced functionality, consider using a <Link url="/operations/new/custom">Custom Tag Operation</Link>.
                                    </p>
                                </Banner>
                            </Card.Section>
                            <Card.Section title="Resource type">
                                <Select options={ResourceTypes} value={this.state.resourceType} onChange={this.onResourceChange} />
                                <RequiredPermissionsWarning requiredPermissions={[resourceTypeToDataPermissions(this.state.resourceType)]} />
                            </Card.Section>
                            <Card.Section title="Select Tags To Fix">
                                <TagViewComponent
                                    showMarkUnmark={true}
                                    showHighlight={false}
                                    canSelect={true}
                                    tags={tags}
                                    selectedTags={this.state.selectedTags}
                                    onSelectedChange={this.handleSelectedTagsChange}
                                    tagActionText="Click on a tag to select it for being merged." />
                            </Card.Section>
                            <Card.Section title="Choose Text To Replace">
                                <Stack vertical>
                                    <Stack.Item>
                                        <TextField
                                            label="Text to replace"
                                            placeholder="Enter text you would like to be replaced"
                                            helpText={
                                                <span>
                                                    Choose which symbols/words/characters to replace
                                                </span>
                                            }
                                            value={this.state.textToReplace}
                                            onChange={this.onTextToReplaceChange}
                                            error={this.state.textToReplace != null && this.state.textToReplace !== "" ? null : "Value is required"}
                                            autoComplete="off"
                                        />
                                    </Stack.Item>
                                    <Stack.Item>
                                        <TextField
                                            label="Replacement text"
                                            placeholder="Enter text that will replace the original text"
                                            helpText={
                                                <span>
                                                    Choose which symbols/words/characters will replace the original text
                                                </span>
                                            }
                                            value={this.state.textReplacement}
                                            onChange={this.onTextReplacementChange}
                                            error={this.state.textReplacement != null && this.state.textReplacement !== "" ? null : "Value is required"}
                                            autoComplete="off"
                                        />
                                    </Stack.Item>
                                    <Stack.Item>
                                        <Select
                                            options={ModeOptions}
                                            value={this.state.mode}
                                            onChange={this.onModeChange}
                                            helpText={
                                                <span>
                                                    Choose search mode - if text is search in the beginning, at the end, or anywhere in the tag text.
                                                </span>
                                            }
                                        />
                                    </Stack.Item>
                                </Stack>
                            </Card.Section>
                            <Card.Section title="Ignore Tag Prefix Settings">
                                <p>You can choose to ignore tag prefixes so that they are not affected by capitalization changes.</p>
                                <ChoiceList
                                    choices={[
                                        {
                                            label: 'Do Not Ignore',
                                            value: null,
                                            helpText: "Prefix will be affected by capitalization changes."
                                        },
                                        {
                                            label: 'Ignore Specific Prefix',
                                            value: 'specific',
                                            helpText: "The prefix you specify will not be affected by capitalization changes.",
                                            renderChildren: this.renderOptions
                                        },
                                        {
                                            label: 'Ignore Prefix By Delimiter',
                                            value: 'delimiter',
                                            helpText: "Any text before the delimiter will be considered as prefix and will not be affected by capitalization changes.",
                                            renderChildren: this.renderOptions
                                        },
                                    ]}

                                    selected={[this.state.tagPrefixBehavior]}
                                    onChange={this.handleTagPrefixBehaviorChange}
                                />
                            </Card.Section>
                        </Card>
                    </Page>
                }

                {this.state.step === "preview" &&
                    <Page title="Preview Changes">
                        <Card
                            title="Preview Changes"
                            primaryFooterAction={{ content: "Execute Changes", disabled: !this.state.preview.canExecute || this.state.isLoading, onAction: this.handleSubmit, loading: this.state.isLoading }}
                            secondaryFooterActions={[{ content: 'Back', disabled: this.state.isLoading, onAction: this.handleBack }]}>
                            <OperationPreviewComponent
                                preview={this.state.preview}
                                resourcePlural={resourcePlural}
                                resourceSingular={resourceSingular} />
                        </Card>
                    </Page>}
            </div>
        );
    }

    renderOptions = (isSelected) => {

        if (!isSelected) {
            return null;
        }

        if (this.state.tagPrefixBehavior === "specific") {
            return <TextField
                value={this.state.tagPrefixBehaviorValue}
                onChange={this.onTagPrefixBehaviorValueChange}
                autoComplete={false}
                clearButton={true}
                onClearButtonClick={() => {
                    this.setState({ tagPrefixBehaviorValue: "" });
                }}
                error={this.state.tagPrefixBehaviorValue == null || this.state.tagPrefixBehaviorValue === "" ? "Value is required" : null}
                placeholder={"Enter prefix to ignore. For example 'vendor_'"}
            />
        }

        if (this.state.tagPrefixBehavior === "delimiter") {
            return <Select
                options={PrefixDelimitCharacterOptions}
                onChange={this.onTagPrefixBehaviorValueChange}
                error={this.state.tagPrefixBehaviorValue == null || this.state.tagPrefixBehaviorValue === "" ? "Value is required" : null}
                value={this.state.tagPrefixBehaviorValue}
            />;
        }

        return null;
    }

    onModeChange = (newValue) => {
        this.setState({
            mode: newValue,
        });
    }

    onTextReplacementChange = (newValue) => {
        this.setState({
            textReplacement: newValue,
        });
    }

    onTextToReplaceChange = (newValue) => {
        this.setState({
            textToReplace: newValue,
        });
    }

    handleCapitalizationTypeChange = (newValue) => {
        this.setState({
            capitalizationType: newValue[0],
        });
    }

    onTagPrefixBehaviorValueChange = (newValue) => {
        this.setState({
            tagPrefixBehaviorValue: newValue,
        });
    }

    handleTagPrefixBehaviorChange = (selected) => {

        let tagPrefixBehaviorValue = "";

        if (selected[0] === "delimiter") {
            tagPrefixBehaviorValue = "Semicolon";
        }

        this.setState({
            tagPrefixBehavior: selected[0],
            tagPrefixBehaviorValue: tagPrefixBehaviorValue
        });
    }

    getTags = () => {

        if (this.state.resourceType === "Product") {
            return this.state.data.tags;
        }

        if (this.state.resourceType === "Customer") {
            return this.state.data.customerTags;
        }

        if (this.state.resourceType === "Order") {
            return this.state.data.orderTags;
        }

        return [];
    }

    onResourceChange = (value) => {
        this.setState({
            resourceType: value,
        });
    }

    handleSelectedTagsChange = (newSelectedTags) => {
        this.setState({
            selectedTags: newSelectedTags
        })
    }

    fetchDashboardData = () => {
        apiFetch('api/dashboard')
            .then(response => { return response.json() })
            .then(data => {
                this.setState({
                    data: data,
                    step: "create",
                    isLoading: false,
                });
            });
    }

    handlePreview = () => {

        this.setState({
            isLoading: true
        });

        apiFetch(`api/scenariosNew/tagTextReplacement/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                selectedTags: this.state.selectedTags,
                replacement: {
                    textToReplace: this.state.textToReplace,
                    textReplacement: this.state.textReplacement,
                    mode: this.state.mode,
                },
                tagPrefixBehavior: this.state.tagPrefixBehavior,
                tagPrefixBehaviorValue: this.state.tagPrefixBehaviorValue,
                resourceType: this.state.resourceType
            })
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    isLoading: false,
                    step: "preview",
                    preview: data
                });
                window.scrollTo(0, 0);
            });
    }

    handleSubmit = () => {
        this.setState({
            isLoading: true
        });

        apiFetch(`api/scenariosNew/tagTextReplacement/execution`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                selectedTags: this.state.selectedTags,
                replacement: {
                    textToReplace: this.state.textToReplace,
                    textReplacement: this.state.textReplacement,
                    mode: this.state.mode,
                },
                tagPrefixBehavior: this.state.tagPrefixBehavior,
                tagPrefixBehaviorValue: this.state.tagPrefixBehaviorValue,
                resourceType: this.state.resourceType
            })
        }).then(_ => {
            this.props.history.push("/operations");
        });
    }

    handleBack = async () => {
        this.setState({
            step: "create"
        })
    }

    componentDidMount() {
        this.fetchDashboardData();
    }
}

export default ScenarioTagTextReplacementScenarioPage;